/**
 * Centralized logging utility
 * Only logs in development mode (except errors)
 * 
 * Security Guidelines:
 * ❌ NEVER log: passwords, API keys, tokens, session IDs, PII
 * ✅ SAFE to log: error messages, component names, actions, IDs
 * 
 * Usage:
 *   logger.debug('Component mounted', { component: 'UserProfile' });
 *   logger.error('API request failed', error);
 *   logger.warn('Deprecated feature used');
 */

const isDev = import.meta.env.DEV;

export const logger = {
  info: (...args: unknown[]) => {
    if (isDev) {
      console.log('[INFO]', ...args);
    }
  },
  
  warn: (...args: unknown[]) => {
    // Only log warnings in development - never in production
    if (isDev) {
      console.warn('[WARN]', ...args);
    }
    // No production logging for warnings - they clutter Lighthouse audits
  },
  
  error: (...args: unknown[]) => {
    // Always log errors, but sanitize in production to avoid [object Object]
    const sanitizeArg = (arg: unknown): string | unknown => {
      if (arg instanceof Error) {
        return arg.message || arg.name || 'Unknown error';
      }
      if (typeof arg === 'object' && arg !== null) {
        // Extract message property if available, otherwise stringify
        const obj = arg as Record<string, unknown>;
        if ('message' in obj && typeof obj.message === 'string') {
          return obj.message;
        }
        try {
          return JSON.stringify(arg);
        } catch {
          return 'Error object';
        }
      }
      return arg;
    };
    
    if (isDev) {
      console.error('[ERROR]', ...args);
    } else {
      // In production, sanitize to readable strings (avoids [object Object])
      console.error('[ERROR]', ...args.map(sanitizeArg));
    }
  },
  
  debug: (...args: unknown[]) => {
    if (isDev) {
      console.debug('[DEBUG]', ...args);
    }
  }
};
